import { ScrollableLayout } from '@/components/Layout';
import Page from '@/components/Layout/Page';
import { AuthContext } from '@/providers/auth';
import { LocationContext } from '@/providers/location';
import { formatDate } from '@/utils/format';
import { useContext } from 'react';
import { ShiftEventSection } from '../components/ShiftEventSection';
import { ShiftInspectionSection } from '../components/ShiftInspectionSection';
import { ShiftQuickActions } from '../components/ShiftQuickActions';
import { useShiftData } from '../hooks/useShiftData';

export const ShiftDashboard = () => {
  const { user } = useContext(AuthContext);
  const { activeLocationId } = useContext(LocationContext);

  // user.user_id defaults to 0 when user is null — the query won't match any staff
  const { events, inspections, depotAddress, isLoading } = useShiftData(
    activeLocationId,
    user?.user_id ?? 0,
  );

  if (!user) return null;

  const todayFormatted = formatDate(new Date().toISOString(), 'EEEE, MMM d');

  return (
    <ScrollableLayout>
      <Page className="py-2 pb-24">
        <div className="px-3 pb-1">
          <h1 className="text-xl font-bold tracking-tight">{`Your Shift, ${user.first_name}`}</h1>
          <p className="text-sm text-muted-foreground -mt-0.5">{todayFormatted}</p>
        </div>

        <Page.Body policy="dashboard.view" className="space-y-5 px-3 py-2">
          {isLoading ? (
            <ShiftLoadingSkeleton />
          ) : (
            <>
              <ShiftQuickActions />
              <ShiftEventSection events={events} depotAddress={depotAddress} />
              {inspections?.length > 0 && <ShiftInspectionSection inspections={inspections} />}
            </>
          )}
        </Page.Body>
      </Page>
    </ScrollableLayout>
  );
};

function ShiftLoadingSkeleton() {
  return (
    <div className="space-y-4 animate-pulse">
      <div className="grid grid-cols-2 gap-2">
        <div className="h-14 rounded-lg bg-muted/40" />
        <div className="h-14 rounded-lg bg-muted/40" />
      </div>
      {[1, 2, 3].map((i) => (
        <div key={i} className="rounded-lg border border-border/40 overflow-hidden">
          <div className="flex">
            <div className="w-[76px] bg-muted/30 border-r border-border/40 p-2">
              <div className="h-6 w-14 rounded bg-muted/50 mx-auto" />
              <div className="h-3 w-10 rounded bg-muted/40 mx-auto mt-1" />
            </div>
            <div className="flex-1 p-2.5 space-y-1.5">
              <div className="flex gap-1.5">
                <div className="h-5 w-16 rounded-full bg-muted/40" />
                <div className="h-5 w-20 rounded-full bg-muted/40" />
              </div>
              <div className="h-5 w-44 rounded bg-muted/50" />
              <div className="h-4 w-32 rounded bg-muted/30" />
            </div>
          </div>
          <div className="flex h-12 border-t border-border/40">
            <div className="flex-1 border-r border-border/40" />
            <div className="flex-1 border-r border-border/40" />
            <div className="flex-1" />
          </div>
        </div>
      ))}
    </div>
  );
}
